import { computed, Injectable, signal } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Message } from '@app/shared/models/app.model';

/**
 * Correction rule interface for defining transcription replacements
 */
export interface CorrectionRule {
  originals: string[];
  replacement: string;
  languages?: string[] | 'all'; // Optional: specific languages or 'all' (default: all)
  licenseUuids?: string[] | 'all'; // Optional: specific license UUIDs or 'all' (default: all)
}

interface CompiledPattern {
  pattern: RegExp;
  replacement: string;
  languages?: string[] | 'all';
  licenseUuids?: string[] | 'all';
}


/**
 * Service for correcting common transcription errors from backend.
 */
@Injectable({
  providedIn: 'root'
})
export class TranscriptionCorrectionService {
  // Correction rules - add new rules here
  private correctionRules = signal<CorrectionRule[]>([
    {
      languages:['nl-NL', 'de-DE'],
      originals:['spixi', 'speexi', 'pixie', 'pixi', 'speaksi', 'speaksy', 'spexy', 'spiexy', 'spectie', 'spiectie', 'spictie', 'speci', 'speciie'],
      replacement:'Speaksee'
    }
  ]);

  // Pre-compiled patterns - auto-updates when rules change
  private compiledPatterns = computed<CompiledPattern[]>(() => {
    const patterns: CompiledPattern[] = [];
    const rules = this.correctionRules();

    for (const rule of rules) {
      for (const original of rule.originals) {
        const pattern = new RegExp(`\\b${this.escapeRegex(original)}\\b`, 'gi');
        patterns.push({
          pattern,
          replacement: rule.replacement,
          languages: rule.languages,
          licenseUuids: rule.licenseUuids
        });
      }
    }

    return patterns;
  });

  constructor() {}

  // Check if rule applies to message language
  private shouldApplyRule(ruleLanguages: string[] | 'all' | undefined, messageLanguage: string): boolean {
    // If no languages specified or 'all', apply to everything
    if (!ruleLanguages || ruleLanguages === 'all') {
      return true;
    }

    // Check if message language matches any of the rule languages
    const msgLang = messageLanguage.toLowerCase();
    return ruleLanguages.some(lang =>
      msgLang === lang.toLowerCase() ||
      msgLang.startsWith(lang.toLowerCase() + '-')
    );
  }

  // Check if rule applies to license UUID
  private shouldApplyLicenseRule(ruleLicenseUuids: string[] | 'all' | undefined, licenseUuid?: string): boolean {
    // If no license UUIDs specified or 'all', apply to everything
    if (!ruleLicenseUuids || ruleLicenseUuids === 'all') {
      return true;
    }

    // If rule has specific licenses but no license UUID provided, don't apply
    if (!licenseUuid) {
      return false;
    }

    // Check if license UUID matches any of the rule license UUIDs
    return ruleLicenseUuids.includes(licenseUuid);
  }

  // Apply corrections to a single message
  private applyCorrections(message: Message, licenseUuid?: string): Message {
    let correctedText = message.message;
    const messageLanguage = message.language || 'en'; // Default to English if not specified

    // Use pre-compiled patterns for better performance
    const patterns = this.compiledPatterns(); // Get current computed patterns
    for (const compiledPattern of patterns) {
      // Skip if rule doesn't apply to this language
      if (!this.shouldApplyRule(compiledPattern.languages, messageLanguage)) {
        continue;
      }

      // Skip if rule doesn't apply to this license
      if (!this.shouldApplyLicenseRule(compiledPattern.licenseUuids, licenseUuid)) {
        continue;
      }

      correctedText = correctedText.replace(compiledPattern.pattern, compiledPattern.replacement);
    }

    // Return message with corrected text
    return {
      ...message,
      message: correctedText
    };
  }

  /**
   * RxJS operator for use in Observable pipelines - main entry point
   */
  public correctMessagesOperator() {
    return (source$: Observable<Message[]>): Observable<Message[]> => {
      return source$.pipe(
        map(messages => messages.map(msg => this.applyCorrections(msg)))
      );
    };
  }

  /**
   * RxJS operator for use in Observable pipelines with license context
   * Use this to apply license-specific correction rules
   */
  public correctMessagesOperatorForLicense(licenseUuid?: string) {
    return (source$: Observable<Message[]>): Observable<Message[]> => {
      return source$.pipe(
        map(messages => messages.map(msg => this.applyCorrections(msg, licenseUuid)))
      );
    };
  }

  // Escape special regex characters
  private escapeRegex(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // Add a new correction rule
  public addCorrectionRule(rule: CorrectionRule): void {
    this.correctionRules.update(rules => [...rules, rule]);
  }

  // Get current correction rules
  public getCorrectionRules(): CorrectionRule[] {
    return [...this.correctionRules()];
  }
}
